import { Admin, CustomRoutes, AuthProvider } from "react-admin";
import { AegleErrorBoundary } from "@aegle/ra-commons";
import { AegleNotification } from "@/components/layout/AegleNotification";
import { queryClient } from "@/config/queryClient";
import { HashRouter, Route } from "react-router-dom";
import { useState, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import dataProviderWithSearch from "./providers/dataProvider/dataProvider";
import {
  theme as defaultTheme,
  darkTheme as defaultDarkTheme,
  createPartnerTheme,
  createPartnerDarkTheme,
} from "./styles/theme/theme";
import getAegleResources from "@/resources/resourceList";
import { StyleGuideRoutes } from "./styles/styleguide";
import { PolicyDashboard } from "./pages/dashboards/policyDashboard/PolicyDashboard";
// Stop-loss claims dashboard hidden until rebuilt — uncomment to re-enable.
import { StoplossClaimsDashboard } from "./pages/dashboards/stoplossClaimsDashboard/StoplossClaimsDashboard";
import { TaskList } from "./resources/tasks/TaskList";
import { Layout } from "./components/layout/Layout";
import { LandingPage } from "./pages/landing/LandingPage";
import { HelpCenter } from "./pages/help/HelpCenter";
import { HelpCategoryList, HelpCategoryCreate, HelpCategoryEdit } from "./resources/help/HelpCategoryManage";
import RecordOwnership from "./pages/dashboards/RecordOwnership/RecordOwnership";
import { EntraIdLoginPage } from "@/components/layout/EntraIdLoginPage";
import { getAuthProvider } from "./providers/authProvider/authProviderInstance";
import AuthInfoPage from "@/resources/admin/AuthInfoPage.tsx";
import Clarity from "@microsoft/clarity";
import { hasAnyPermission } from "./utils/permissions";
import { NotFound } from "./pages/NotFound";
import { Permission } from "./types";
import { AUTH_CONSTANTS } from "@/constants";
import {
  PartnerProvider,
  usePartner,
  PartnerLoadingScreen,
  PartnerErrorScreen,
} from "./providers/partner/PartnerProvider";
import { createDynamicAuthProvider } from "./providers/authProvider/dynamicAuthProviderInstance";
import { setActiveAuthProvider } from "./providers/authProvider/activeAuthProvider";
import { PartnerResolveResponse } from "./services/partnerResolution";
import RecordRecovery from "./pages/admin/RecordRecovery";

// Check if dynamic partner authentication is enabled
const USE_DYNAMIC_PARTNER_AUTH = import.meta.env.VITE_USE_DYNAMIC_PARTNER_AUTH === "true";

// MS Clarity - only initialize if project ID is configured
const clarityProjectId = import.meta.env.VITE_MS_CLARITY_PROJECT_ID;
if (clarityProjectId) {
  Clarity.init(clarityProjectId);
}

/**
 * Reads cached partner branding synchronously to prevent a flash
 * of default-themed UI on page reload.
 */
const getInitialThemes = () => {
  try {
    const cached = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_BRANDING);
    if (!cached) return { theme: defaultTheme, darkTheme: defaultDarkTheme };

    const branding = JSON.parse(cached);
    if (!branding.brand_primary) return { theme: defaultTheme, darkTheme: defaultDarkTheme };

    return {
      theme: createPartnerTheme(branding),
      darkTheme: createPartnerDarkTheme(branding),
    };
  } catch {
    return { theme: defaultTheme, darkTheme: defaultDarkTheme };
  }
};

const { theme: initialTheme, darkTheme: initialDarkTheme } = getInitialThemes();

/**
 * Filters resources based on user permissions.
 */
const getFilteredResources = (permissions: Permission[]) => {
  if (!permissions) {
    return getAegleResources;
  }

  return getAegleResources.filter((resource) => {
    const resourceName = resource.props.name;
    return hasAnyPermission(permissions, resourceName);
  });
};

/**
 * Inner app component that has access to partner context (when enabled)
 */
const InnerApp = ({ partner }: { partner?: PartnerResolveResponse }) => {
  const [authProvider, setAuthProvider] = useState<AuthProvider | null>(null);
  const [isInitializingAuth, setIsInitializingAuth] = useState(true);
  const [theme, setTheme] = useState(initialTheme);
  const [darkTheme, setDarkTheme] = useState(initialDarkTheme);

  // Listen for branding updates (dispatched by InPlaceColorEditor / PartnerBrandingTab)
  // and recompute theme from the updated sessionStorage cache.
  useEffect(() => {
    const handleBrandingUpdate = () => {
      const { theme: t, darkTheme: dt } = getInitialThemes();
      setTheme(t);
      setDarkTheme(dt);
    };
    window.addEventListener("partner-branding-updated", handleBrandingUpdate);
    return () => window.removeEventListener("partner-branding-updated", handleBrandingUpdate);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        let provider: AuthProvider;

        const hasDedicatedAuth =
          USE_DYNAMIC_PARTNER_AUTH &&
          partner &&
          partner.authConfig.mode === "dedicated" &&
          partner.authConfig.clientId &&
          partner.authConfig.authority;

        if (hasDedicatedAuth) {
          console.log("[App] Creating dynamic auth provider with partner config:", {
            clientId: partner.authConfig.clientId,
            authority: partner.authConfig.authority,
            mode: partner.authConfig.mode,
            customDomain: partner.authConfig.customDomain,
          });
          provider = createDynamicAuthProvider(partner.authConfig, { apiBaseUrl: partner.apiBaseUrl });
        } else {
          if (USE_DYNAMIC_PARTNER_AUTH && partner) {
            console.log("[App] Partner resolved as shared/incomplete — falling back to static auth provider");
          } else {
            console.log("[App] Using static auth provider");
          }
          provider = getAuthProvider();
        }

        console.log("[App] Auth provider created, handling redirect callback...");

        // Handle MSAL redirect if applicable
        if (provider.handleRedirectCallback) {
          await provider.handleRedirectCallback();
          console.log("[App] Redirect callback complete");
        }

        console.log("[App] Setting auth provider in state");
        setActiveAuthProvider(provider);
        setAuthProvider(provider);
      } catch (error) {
        console.error("[App] Error initializing auth:", error);
      } finally {
        console.log("[App] Auth initialization finished, isInitializingAuth → false");
        setIsInitializingAuth(false);
      }
    };

    void initializeAuth();
  }, [partner]);

  // Show loading while auth is being initialized
  if (isInitializingAuth || !authProvider) {
    console.log(
      "[App] Rendering loading state — isInitializingAuth:",
      isInitializingAuth,
      "authProvider:",
      !!authProvider
    );
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div>Loading...</div>
      </ThemeProvider>
    );
  }

  return (
    <HashRouter>
      <Admin
        layout={Layout}
        error={AegleErrorBoundary}
        notification={AegleNotification}
        queryClient={queryClient}
        dataProvider={dataProviderWithSearch}
        authProvider={authProvider}
        requireAuth
        dashboard={LandingPage}
        theme={theme}
        darkTheme={darkTheme}
        loginPage={EntraIdLoginPage}
        catchAll={NotFound}
        title={partner?.partnerName || "Coronis Management"}
      >
        {(permissions) => getFilteredResources(permissions)}
        <CustomRoutes>
          <Route path="/styleguide/*" element={<StyleGuideRoutes />} />
          <Route path="/policies/dashboard" element={<PolicyDashboard />} />
          <Route path="/stoploss-claims/dashboard" element={<StoplossClaimsDashboard />} />
          <Route path="/tasks" element={<TaskList />} />
          <Route path="/record-management" element={<RecordOwnership />} />
          <Route path="/auth-info" element={<AuthInfoPage />} />
          <Route path="/record-recovery" element={<RecordRecovery />} />
          <Route path="/help" element={<HelpCenter />} />
          {/* KB category management (N7, #5430). Static /create outranks /:id in
              react-router v6, so order is not significant. */}
          <Route path="/help-categories" element={<HelpCategoryList />} />
          <Route path="/help-categories/create" element={<HelpCategoryCreate />} />
          <Route path="/help-categories/:id" element={<HelpCategoryEdit />} />
        </CustomRoutes>
      </Admin>
    </HashRouter>
  );
};

/**
 * Component that uses partner context when dynamic auth is enabled
 */
const AppWithPartnerContext = () => {
  const { partner, isLoading, error } = usePartner();

  // Show loading screen while partner is being resolved
  if (isLoading) {
    return <PartnerLoadingScreen />;
  }

  // Show error screen if partner resolution failed
  if (error) {
    return <PartnerErrorScreen error={error} onRetry={() => window.location.reload()} />;
  }

  return <InnerApp partner={partner || undefined} />;
};

/**
 * Main App component - conditionally uses partner resolution
 */
export const App = () => {
  if (USE_DYNAMIC_PARTNER_AUTH) {
    return (
      <PartnerProvider>
        <AppWithPartnerContext />
      </PartnerProvider>
    );
  }

  // Static auth mode - no partner resolution
  return <InnerApp />;
};
